import { useState, useMemo } from 'react';
import { DataStandard, EnhancedSchemaField } from '@/lib/data-standards/schemas';
import { CodeBlock } from '@/components/docs/CodeBlock';
import { SchemaValidator } from './SchemaValidator';
import { FieldRow } from './FieldRow';
import { EnumPopover } from './EnumPopover';
import { ExportDialog } from './ExportDialog';
import { useFieldLookups } from '@/hooks/useFieldLookups';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Check, Minus, Play, ChevronRight, Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import { parseFieldType, extractNestedFields, getTypeColors, getFormatColors } from '@/lib/data-standards/field-utils';

interface SchemaViewerProps {
  schema: DataStandard;
}

export function SchemaViewer({ schema }: SchemaViewerProps) {
  const [activeTab, setActiveTab] = useState('example');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // Collect all lookupGroups from fields for batch fetching
  const lookupGroups = useMemo(() => {
    return schema.fields
      .filter((f) => f.lookupGroup)
      .map((f) => f.lookupGroup as string);
  }, [schema.fields]);

  // Fetch lookup values for enum resolution
  const { data: lookupData } = useFieldLookups(lookupGroups);

  // Enhance fields with parsed type info - database values first, JSON schema as fallback
  const enhancedFields = useMemo((): EnhancedSchemaField[] => {
    const schemaObj = schema.schema as Record<string, unknown>;
    const properties = (schemaObj?.properties || {}) as Record<string, unknown>;
    const requiredFields = (schemaObj?.required || []) as string[];

    return schema.fields.map((field) => {
      const { baseType } = parseFieldType(field.type);
      const propSchema = properties[field.name] as Record<string, unknown> | undefined;

      // Extract nested fields from JSON schema (nested objects not in field_library yet)
      const nestedFields = extractNestedFields(schema.schema, field.name);

      // Convert nested fields to EnhancedSchemaField format
      const enhancedNested: EnhancedSchemaField[] | undefined = nestedFields?.map((nf) => ({
        name: nf.name,
        type: nf.type,
        description: nf.description || '',
        required: nf.required,
        baseType: parseFieldType(nf.type).baseType,
        format: nf.format,
        constraints: nf.constraints,
        enumValues: nf.enumValues,
        nestedFields: nf.nestedFields?.map((nnf) => ({
          name: nnf.name,
          type: nnf.type,
          description: nnf.description || '',
          required: nnf.required,
          baseType: parseFieldType(nnf.type).baseType,
          format: nnf.format,
          constraints: nnf.constraints,
          enumValues: nnf.enumValues,
        })),
      }));

      // Resolve enum values: prefer lookup data, fallback to JSON schema
      const enumValues = field.lookupGroup && lookupData?.[field.lookupGroup]
        ? lookupData[field.lookupGroup]
        : (propSchema?.enum as string[] | undefined);

      // Build constraints - use database values first, JSON schema as fallback
      const jsonConstraints = propSchema
        ? {
            minLength: propSchema.minLength as number | undefined,
            maxLength: propSchema.maxLength as number | undefined,
            minimum: propSchema.minimum as number | undefined,
            maximum: propSchema.maximum as number | undefined,
            pattern: propSchema.pattern as string | undefined,
          }
        : undefined;

      return {
        ...field, // Spread all database values (format, constraints, lookupGroup)
        baseType,
        // Use database format first, fallback to JSON schema
        format: field.format || (propSchema?.format as string | undefined),
        // Use database constraints first, fallback to JSON schema
        constraints: field.constraints || jsonConstraints,
        enumValues,
        nestedFields: enhancedNested,
        // Preserve lookupGroup for reference
        lookupGroup: field.lookupGroup,
        // Use database required first, fallback to JSON schema required array
        required: field.required ?? requiredFields.includes(field.name),
      };
    });
  }, [schema, lookupData]);

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2 md:gap-3">
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            <h1 className="text-2xl md:text-3xl font-bold">{schema.title}</h1>
            <Badge variant="secondary">v{schema.version}</Badge>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExportDialogOpen(true)}
            className="shrink-0"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
        <p className="text-muted-foreground text-base md:text-lg">{schema.description}</p>
        <p className="text-xs text-muted-foreground">Last updated: {schema.lastUpdated}</p>
      </div>

      {/* Export Dialog */}
      <ExportDialog
        standard={schema}
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
      />

      {/* Schema/Example Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="w-full sm:w-auto inline-flex">
            <TabsTrigger value="example" className="text-xs sm:text-sm">
              <span className="hidden sm:inline">Example Data</span>
              <span className="sm:hidden">Example</span>
            </TabsTrigger>
            <TabsTrigger value="schema" className="text-xs sm:text-sm">
              <span className="hidden sm:inline">JSON Schema</span>
              <span className="sm:hidden">Schema</span>
            </TabsTrigger>
            <TabsTrigger value="validate" className="text-xs sm:text-sm flex items-center gap-1.5">
              <Play className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Validate</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="example" className="mt-4">
          <CodeBlock
            code={JSON.stringify(schema.example, null, 2)}
            language="json"
            title={`${schema.title} Example`}
            showLineNumbers
          />
        </TabsContent>

        <TabsContent value="schema" className="mt-4">
          <CodeBlock
            code={JSON.stringify(schema.schema, null, 2)}
            language="json"
            title={`${schema.title} JSON Schema`}
            showLineNumbers
          />
        </TabsContent>

        <TabsContent value="validate" className="mt-4">
          <SchemaValidator
            schema={schema.schema}
            example={schema.example}
            schemaTitle={schema.title}
          />
        </TabsContent>
      </Tabs>

      {/* Field Documentation */}
      <div className="space-y-3 md:space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg md:text-xl font-semibold">Field Reference</h2>
          <Badge variant="outline" className="text-xs">
            {enhancedFields.length} fields
          </Badge>
        </div>

        {/* Desktop table - hidden on mobile */}
        <div className="hidden md:block border rounded-lg overflow-x-auto">
          <Table className="table-fixed min-w-[700px]">
            <TableHeader>
              <TableRow className="bg-muted/50 [&>th]:py-2">
                <TableHead className="w-[22%] min-w-[140px]">Field</TableHead>
                <TableHead className="w-[20%] min-w-[120px]">Type</TableHead>
                <TableHead className="w-[10%] min-w-[70px] text-center">Required</TableHead>
                <TableHead className="w-[48%]">Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {enhancedFields.map((field) => (
                <FieldRow key={field.name} field={field} schema={schema.schema} />
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile accordion view - visible only on mobile */}
        <div className="md:hidden space-y-2">
          {enhancedFields.map((field) => (
            <MobileFieldCard key={field.name} field={field} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Mobile-specific field card component
function MobileFieldCard({ field }: { field: EnhancedSchemaField }) {
  const [isOpen, setIsOpen] = useState(false);

  const typeColors = getTypeColors(field.type);
  const formatColors = field.format ? getFormatColors(field.format) : null;

  const hasDetails =
    field.nestedFields?.length ||
    field.constraints?.minLength ||
    field.constraints?.maxLength ||
    field.constraints?.minimum ||
    field.constraints?.maximum ||
    field.constraints?.pattern ||
    field.example !== undefined ||
    field.enumValues?.length;

  const constraintsList: string[] = [];
  if (field.constraints?.minLength !== undefined) {
    constraintsList.push(`minLength: ${field.constraints.minLength}`);
  }
  if (field.constraints?.maxLength !== undefined) {
    constraintsList.push(`maxLength: ${field.constraints.maxLength}`);
  }
  if (field.constraints?.minimum !== undefined) {
    constraintsList.push(`min: ${field.constraints.minimum}`);
  }
  if (field.constraints?.maximum !== undefined) {
    constraintsList.push(`max: ${field.constraints.maximum}`);
  }
  if (field.constraints?.pattern) {
    constraintsList.push(`pattern`);
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg bg-card overflow-hidden">
        <CollapsibleTrigger
          className="w-full p-2.5 text-left"
          disabled={!hasDetails}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {hasDetails && (
                <ChevronRight
                  className={cn(
                    'h-4 w-4 text-muted-foreground transition-transform shrink-0',
                    isOpen && 'rotate-90'
                  )}
                />
              )}
              <code className="text-sm font-semibold font-mono truncate">
                {field.name}
              </code>
            </div>
            <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
              <Badge
                variant="outline"
                className={cn(
                  'text-xs font-mono',
                  typeColors.bg,
                  typeColors.text,
                  typeColors.border
                )}
              >
                {field.baseType || field.type}
              </Badge>
              {field.format && formatColors && (
                <Badge
                  variant="outline"
                  className={cn('text-xs', formatColors.bg, formatColors.text)}
                >
                  {field.format}
                </Badge>
              )}
              {field.required && (
                <Badge className="bg-green-500/10 text-green-600 text-xs border border-green-500/20">
                  Required
                </Badge>
              )}
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">
            {field.description}
          </p>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-2.5 pb-2.5 pt-0 space-y-2.5 border-t">
            {/* Enum values */}
            {field.enumValues && field.enumValues.length > 0 && (
              <div className="pt-3">
                <span className="text-xs font-medium text-muted-foreground block mb-1.5">
                  Accepted Values:
                </span>
                <div className="flex flex-wrap gap-1">
                  {field.enumValues.slice(0, 5).map((v) => (
                    <Badge key={v} variant="secondary" className="text-xs font-mono">
                      {v}
                    </Badge>
                  ))}
                  {field.enumValues.length > 5 && (
                    <EnumPopover values={field.enumValues} fieldName={field.name}>
                      <Badge
                        variant="outline"
                        className="text-xs cursor-pointer bg-pink-500/10 text-pink-600 border-pink-500/20"
                      >
                        +{field.enumValues.length - 5} more
                      </Badge>
                    </EnumPopover>
                  )}
                </div>
              </div>
            )}

            {/* Constraints */}
            {constraintsList.length > 0 && (
              <div className="pt-3">
                <span className="text-xs font-medium text-muted-foreground block mb-1.5">
                  Constraints:
                </span>
                <div className="flex flex-wrap gap-1">
                  {constraintsList.map((c, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs font-mono">
                      {c}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Example */}
            {field.example !== undefined && (
              <div className="pt-3">
                <span className="text-xs font-medium text-muted-foreground block mb-1.5">
                  Example:
                </span>
                <code className="text-xs bg-muted px-2 py-1 rounded font-mono block break-all">
                  {typeof field.example === 'object'
                    ? JSON.stringify(field.example)
                    : String(field.example)}
                </code>
              </div>
            )}

            {/* Nested fields */}
            {field.nestedFields && field.nestedFields.length > 0 && (
              <div className="pt-3">
                <span className="text-xs font-medium text-muted-foreground block mb-2">
                  Nested Fields:
                </span>
                <div className="space-y-2 pl-2 border-l-2 border-muted">
                  {field.nestedFields.map((nf) => (
                    <div key={nf.name} className="text-sm">
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-xs">{nf.name}</code>
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-xs font-mono',
                            getTypeColors(nf.type).bg,
                            getTypeColors(nf.type).text
                          )}
                        >
                          {nf.baseType || nf.type}
                        </Badge>
                        {nf.required && (
                          <Check className="h-3 w-3 text-green-500" />
                        )}
                      </div>
                      {nf.description && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {nf.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
